import React, { useState, useEffect } from 'react';
import { Switch, FormControlLabel, Typography, Box } from '@mui/material';
import WidgetWrapper from './WidgetWrapper';
import tagService from '../services/tagService';
import { useWorkflowExecution } from '../hooks/useWorkflowExecution';

const ToggleSwitch = ({ 
  checked = false,
  label = '',
  disabled = false,
  size = 'medium', // 'small', 'medium'
  color = 'primary', // 'primary', 'secondary', 'success', 'error', 'warning', 'info'
  tagId = null,
  tagValue = null, // Will be injected by preview mode
  onChange,
  // Workflow execution properties
  enableWorkflow = false,
  workflowName = '',
  workflowParameters = {},
  companyId = 1,
  enableLogging = false,
  watchdogTimeout = null,
  isEditMode = false,
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props 
}) => {
  const { executeWorkflow, isExecuting } = useWorkflowExecution();

  // Use tag value if available, otherwise use static value
  const currentValue = tagValue !== null && tagValue !== undefined ? Boolean(tagValue) : checked;
  const [localValue, setLocalValue] = useState(currentValue);

  useEffect(() => {
    setLocalValue(currentValue);
  }, [currentValue]);

  // Context menu handler - dispatches global event
  const handleContextMenu = (event) => {
    if (tagId) {
      event.preventDefault();
      event.stopPropagation();

      // Dispatch global event to open Tag Info Modal
      const tagInfoData = {
        tagId: tagId,
        tagName: label || `Switch ${tagId}`,
        tagName2: '',
        currentValue: localValue ? 1 : 0,
        unit: ''
      };
      window.dispatchEvent(new CustomEvent('openTagInfo', { detail: tagInfoData }));
    } else {
    }
  };

  const handleChange = async (event) => {
    const newValue = event.target.checked;
    setLocalValue(newValue);

    // Skip actions in edit mode
    if (isEditMode) {
      return;
    }

    // Execute workflow if enabled
    if (enableWorkflow && workflowName) {
      try {
        // Add switch value to workflow parameters
        const parameters = {
          ...workflowParameters,
          switchValue: newValue,
          toggleState: newValue
        };

        await executeWorkflow(
          workflowName,
          parameters,
          companyId,
          enableLogging,
          watchdogTimeout
        );


        // Show success notification
        if (window.showNotification) {
          window.showNotification(`Workflow "${workflowName}" completed successfully`, 'success');
        }

      } catch (error) {
        console.error('Failed to execute switch workflow:', error);

        // Revert switch value on workflow failure
        setLocalValue(currentValue);

        // Show error notification
        if (window.showNotification) {
          window.showNotification(`Failed to execute workflow "${workflowName}": ${error.message}`, 'error');
        } else {
          alert(`Failed to execute workflow "${workflowName}": ${error.message}`);
        }
        return;
      }
    }

    // Update tag if connected
    if (tagId) {
      try {
        await tagService.updateTagValue(tagId, newValue);
      } catch (error) {
        console.error('Failed to update tag value:', error);
        // Revert on error
        setLocalValue(currentValue);
      }
    }

    // Call external onChange if provided
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <WidgetWrapper
      backgroundColor={backgroundColor}
      foreColor={foreColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      justifyContent="flex-start"
      alignItems="center"
      padding="8px"
      onContextMenu={handleContextMenu}
      {...props}
    >
      {label ? (
        <FormControlLabel
          control={
            <Switch
              checked={localValue}
              onChange={handleChange}
              disabled={disabled || isExecuting || isEditMode}
              size={size}
              color={color}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: foreColor || undefined
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: foreColor || undefined
                }
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontSize: fontSize ? `${fontSize}px` : '10px',
                fontWeight: 'inherit',
                fontFamily: 'inherit',
                color: 'inherit'
              }}
            >
              {label}
            </Typography>
          }
          labelPlacement="end"
        />
      ) : (
        <Switch
          checked={localValue}
          onChange={handleChange}
          disabled={disabled}
          size={size}
          color={color}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: foreColor || undefined
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: foreColor || undefined
            }
          }}
        />
      )}
    </WidgetWrapper>
  );
};

export default ToggleSwitch;